// Recovery actions page — registry-driven. Fetches /api/actions/schema,
// renders one collapsible card per action. Submitting routes by
// `dispatchSite`:
//   - "backend" → POST /api/actions/{slug}/build, then sign with the
//     wallet whose chain matches the response variant.
//   - "frontend" → call bridge-sdk-js client-side, then sign with the
//     wallet attached to the chain prefix in the form body.

import { useMemo, useState } from "react";
import { useQuery } from "@tanstack/react-query";
import {
  useAccount as useEvmAccount,
  useSendTransaction,
  useSwitchChain,
  useWalletClient,
} from "wagmi";
import {
  useConnection as useSolConnection,
  useWallet as useSolWallet,
} from "@solana/wallet-adapter-react";
import { useAccount as useStarkAccount, useSendTransaction as useStarkSend } from "@starknet-react/core";
import clsx from "clsx";
import {
  ApiClientError,
  actionSchema,
  buildAction,
} from "@/api/client";
import { useNearWallet } from "@/wallet/near";
import { useBtcWallet } from "@/wallet/btc";
import { useHealth } from "@/hooks/useHealth";
import { ActionForm, type FormBody } from "@/components/ActionForm";
import { ResultCard, type ActionOutcome } from "@/components/ResultCard";
import { chainForAction, dispatchSite } from "@/api/dispatch";
import { dispatchFrontend } from "@/wallet/dispatchFrontend";
import { nearExplorerTx } from "@/lib/explorer";
import { friendlyWalletError } from "@/lib/walletError";
import type { ActionCategory, ActionDescriptor } from "@/types/dto";

const CATEGORY_LABELS: Record<ActionCategory, string> = {
  initialize: "Initialize",
  sign: "Sign",
  finalize: "Finalize",
  maintenance: "UTXO maintenance",
  diagnostic: "Diagnostics",
};

const CATEGORY_ORDER: ActionCategory[] = [
  "initialize",
  "sign",
  "finalize",
  "maintenance",
  "diagnostic",
];

export function RecoveryPage() {
  const schemaQ = useQuery({
    queryKey: ["action-schema"],
    queryFn: ({ signal }) => actionSchema(signal),
    staleTime: 5 * 60_000,
  });

  const grouped = useMemo(() => {
    const out = new Map<ActionCategory, ActionDescriptor[]>();
    for (const d of schemaQ.data ?? []) {
      const list = out.get(d.category) ?? [];
      list.push(d);
      out.set(d.category, list);
    }
    return out;
  }, [schemaQ.data]);

  return (
    <section className="space-y-8" data-testid="recovery-page">
      <header>
        <h1 className="text-2xl font-semibold tracking-tight text-slate-900 dark:text-slate-100">Recovery actions</h1>
        <p className="text-sm text-slate-600 dark:text-slate-400">
          Pick a flow, fill the form, sign with your wallet — the resulting transaction.
        </p>
      </header>
      {schemaQ.isError && (
        <div className="rounded-md border border-red-300 dark:border-red-800 bg-red-50 dark:bg-red-900/40 px-3 py-2 text-sm text-red-700 dark:text-red-200">
          Failed to load action schema: {(schemaQ.error as Error).message}
        </div>
      )}
      {CATEGORY_ORDER.map((cat) => {
          const list = grouped.get(cat);
          if (!list || list.length === 0) return null;
          return (
            <div key={cat} className="space-y-3">
              <h2 className="text-sm font-semibold uppercase tracking-wider text-slate-500">
                {CATEGORY_LABELS[cat]}
              </h2>
              <div className="space-y-2">
                {list.map((d) => (
                  <ActionCard key={d.slug} descriptor={d} />
                ))}
              </div>
            </div>
          );
      })}
    </section>
  );
}

function ActionCard({ descriptor }: { descriptor: ActionDescriptor }) {
  const [open, setOpen] = useState(false);
  const [outcome, setOutcome] = useState<ActionOutcome | null>(null);
  const [body, setBody] = useState<FormBody>({});
  const [formError, setFormError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  // Only surface validation errors after the operator tries to submit — not
  // while they're still typing the args.
  const [triedSubmit, setTriedSubmit] = useState(false);

  const { network } = useHealth();
  const near = useNearWallet();
  const evm = useEvmAccount();
  const evmWalletClient = useWalletClient();
  const evmSend = useSendTransaction();
  const evmSwitch = useSwitchChain();
  const sol = useSolWallet();
  const solConn = useSolConnection();
  const stark = useStarkAccount();
  const starkSend = useStarkSend({ calls: [] });
  const btc = useBtcWallet();

  const requiredWallet = useMemo(() => chainForAction(descriptor, body), [descriptor, body]);
  const walletReady = useMemo(() => {
    switch (requiredWallet) {
      case "view":
        return true;
      case "near":
        return !!near.account;
      case "evm":
        return evm.isConnected;
      case "sol":
        return !!sol.publicKey;
      case "starknet":
        return !!stark.address;
      case "btc":
        return !!btc.account;
    }
  }, [requiredWallet, near.account, evm.isConnected, sol.publicKey, stark.address, btc.account]);

  const submit = async () => {
    setTriedSubmit(true);
    if (formError) {
      setOutcome({ kind: "errored", message: formError });
      return;
    }
    setBusy(true);
    setOutcome({ kind: "pending", message: "Building transaction…" });
    try {
      const site = dispatchSite(descriptor.kind, body);
      if (site === "frontend") {
        const next = await dispatchFrontend({
          descriptor,
          body,
          network,
          wallets: {
            evm: {
              walletClient: evmWalletClient.data,
              sendTransaction: evmSend.sendTransactionAsync,
              currentChainId: evm.chainId,
              switchChain: evmSwitch.switchChainAsync,
            },
            sol: { wallet: sol, connection: solConn.connection },
            starknet: { sendAsync: starkSend.sendAsync },
            near: { account: near.account, signAndSend: near.signAndSend },
          },
        });
        setOutcome(next);
      } else {
        const resp = await buildAction(descriptor.slug, body);
        if (resp.kind === "view_result") {
          setOutcome({ kind: "view", value: resp.result });
        } else if (resp.kind === "unsigned_txs") {
          setOutcome({ kind: "pending", message: "Awaiting wallet signature…" });
          const txs = resp.unsignedTxs;
          if (txs.length === 0) {
            setOutcome({ kind: "errored", message: "Backend returned no unsigned transactions." });
          } else if (txs.length === 1) {
            const hash = await near.signAndSend(txs[0]!);
            setOutcome({
              kind: "tx",
              hash,
              explorerUrl: nearExplorerTx(network, hash),
            });
          } else {
            const hashes = await near.signAndSendBatch(txs);
            const [first, ...rest] = hashes;
            if (!first) {
              setOutcome({
                kind: "errored",
                message: "Wallet returned no transaction hashes for the batch.",
              });
            } else {
              setOutcome({
                kind: "tx",
                hash: first,
                explorerUrl: nearExplorerTx(network, first),
                extra: rest.map((hash, i) => ({
                  hash,
                  label: `#${i + 2}`,
                  explorerUrl: nearExplorerTx(network, hash),
                })),
              });
            }
          }
        } else {
          // Backend shouldn't return a chain-side variant for a route
          // marked "backend" — this is dead code in healthy state but
          // we still cover it for completeness.
          setOutcome({ kind: "errored", message: `Unexpected response shape: ${resp.kind}` });
        }
      }
    } catch (e) {
      if (e instanceof ApiClientError && e.needsBridgeCli) {
        setOutcome({ kind: "needs_bridge_cli", message: e.message });
      } else if (e instanceof ApiClientError) {
        // Backend's own ApiError — surface the message verbatim, it's
        // already operator-tuned ("vout 0 pays to `tm…` which the
        // bridge indexer doesn't track" etc.).
        setOutcome({ kind: "errored", message: e.message });
      } else {
        // Wallet-side rejection / RPC error — collapse to a one-line
        // friendly form (raw viem/wallet messages are huge).
        setOutcome({ kind: "errored", message: friendlyWalletError(e) });
      }
    } finally {
      setBusy(false);
    }
  };

  // Network-aware placeholder substitution.
  const placeholderHook = (raw: string): string => {
    if (network === "mainnet") {
      return raw.replaceAll(".testnet", ".near");
    }
    return raw;
  };

  const explorerSummary = useMemo(() => {
    if (!outcome || outcome.kind !== "tx") return outcome;
    // Some flows hand us a non-NEAR hash; the dispatchers already
    // populate explorerUrl correctly. This block is for the case where
    // an inline tx outcome got built without it — defensive only.
    if (outcome.explorerUrl) return outcome;
    return {
      ...outcome,
      explorerUrl: nearExplorerTx(network, outcome.hash),
    };
  }, [outcome, network]);

  const connectorsBlurb = descriptor.connectors.length
    ? descriptor.connectors.join(", ")
    : "any connector";

  return (
    <article
      className="overflow-hidden rounded-md border border-slate-200 dark:border-slate-800 bg-white dark:bg-slate-900"
      data-testid={`action-card-${descriptor.slug}`}
    >
      <button
        type="button"
        onClick={() => setOpen((v) => !v)}
        className="flex w-full items-center justify-between gap-3 px-4 py-3 text-left hover:bg-slate-100/70 dark:hover:bg-slate-800/60"
      >
        <div className="flex items-start gap-3">
          <span
            className={clsx(
              // `shrink-0` keeps the 8×8 dot circular — without it, flex
              // compresses its width when the label/help text is long
              // (finalize / fast-finalize / deploy-token), squashing the
              // circle into an oval.
              "mt-1 inline-block h-2 w-2 shrink-0 rounded-full",
              open ? "bg-near-green" : "bg-slate-300 dark:bg-slate-600",
            )}
          />
          <div>
            <div className="font-medium text-slate-900 dark:text-slate-100">{descriptor.label}</div>
            <div className="mt-0.5 text-xs text-slate-600 dark:text-slate-400">{descriptor.help}</div>
          </div>
        </div>
        <span className="shrink-0 rounded bg-slate-100 dark:bg-slate-800 px-2 py-0.5 text-center font-mono text-[10px] text-slate-600 dark:text-slate-400 whitespace-nowrap">
          {connectorsBlurb}
        </span>
      </button>
      {open && (
        <div className="space-y-3 border-t border-slate-200 dark:border-slate-800 px-4 py-3">
          <ActionForm
            params={descriptor.params}
            placeholderHook={placeholderHook}
            network={network}
            onChange={(b, err) => {
              setBody(b);
              setFormError(err);
            }}
          />
          {requiredWallet === "sol" && network === "testnet" && (
            <p className="rounded-md bg-slate-100/70 dark:bg-slate-800/60 px-3 py-2 text-xs text-slate-600 dark:text-slate-400">
              Testnet SVM transfers finalize on Solana <strong>devnet</strong> — set your Solana
              wallet (Phantom / Solflare / …) to <strong>Devnet</strong> before signing, or the
              wallet will warn about a network mismatch.
            </p>
          )}
          <div className="flex items-center gap-3">
            <button
              type="button"
              disabled={busy || !walletReady}
              onClick={() => void submit()}
              className="rounded-md bg-near-green px-4 py-2 text-sm font-semibold text-slate-950 hover:bg-near-green-dark disabled:cursor-not-allowed disabled:opacity-50"
              data-testid={`submit-${descriptor.slug}`}
            >
              {busy ? "Working…" : "Submit"}
            </button>
            {!walletReady && requiredWallet !== "view" && (
              <span className="text-xs text-amber-800 dark:text-amber-300">
                Connect a {requiredWallet.toUpperCase()} wallet to submit
              </span>
            )}
            {triedSubmit && formError && <span className="text-xs text-red-700 dark:text-red-300">{formError}</span>}
          </div>
          {explorerSummary && <ResultCard outcome={explorerSummary} />}
        </div>
      )}
    </article>
  );
}
